/**
 * Widget-system layout core (POS2-877): widget ids, the default order, and the
 * pure layout operations behind the dashboard's customisable tail. The registry
 * (components/dashboard/widget-registry.tsx) maps these ids to titles and
 * renderers; this module stays render-free so the layout logic is unit-testable.
 */

/** Tail widgets shown by default, in default order (design §11). */
export const DEFAULT_LAYOUT = ["money", "when", "working", "examine"] as const;

/** Legacy Phase 5 widgets, available from the add-widget card but default off. */
export const LIBRARY_WIDGETS = [
  "waste-type-donut",
  "service-donut",
  "tier-bar",
  "category-bar",
  "l1-breakdown",
] as const;

export type WidgetId = (typeof DEFAULT_LAYOUT)[number] | (typeof LIBRARY_WIDGETS)[number];

export const ALL_WIDGET_IDS: readonly WidgetId[] = [...DEFAULT_LAYOUT, ...LIBRARY_WIDGETS];

function isWidgetId(value: unknown): value is WidgetId {
  return typeof value === "string" && (ALL_WIDGET_IDS as readonly string[]).includes(value);
}

/**
 * A stored layout cleaned against the registry: unknown ids dropped (the
 * registry is truth, so retired widgets need no data migration), duplicates
 * keep their first position. Anything that isn't an array → null = "use the
 * default order". An empty array is a real state (user removed every widget).
 */
export function sanitizeLayout(value: unknown): WidgetId[] | null {
  if (!Array.isArray(value)) return null;
  const seen = new Set<WidgetId>();
  for (const id of value) if (isWidgetId(id)) seen.add(id);
  return [...seen];
}

/** The widget one step up (-1) or down (+1); no-op at the ends or when absent. */
export function moveWidget(layout: readonly WidgetId[], id: WidgetId, delta: -1 | 1): WidgetId[] {
  const from = layout.indexOf(id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= layout.length) return [...layout];
  const next = [...layout];
  next[from] = next[to];
  next[to] = id;
  return next;
}

export function removeWidget(layout: readonly WidgetId[], id: WidgetId): WidgetId[] {
  return layout.filter((w) => w !== id);
}

/** Appends at the end — the mock's re-add position. No-op when already shown. */
export function addWidget(layout: readonly WidgetId[], id: WidgetId): WidgetId[] {
  return layout.includes(id) ? [...layout] : [...layout, id];
}

/** Everything registered but not on view — the add-widget card's chip list. */
export function addableWidgets(layout: readonly WidgetId[]): WidgetId[] {
  return ALL_WIDGET_IDS.filter((id) => !layout.includes(id));
}
